/* eslint-disable no-console */
import {isError} from 'lodash';

import store from '~/shared/store';
import {actions as errorDialogDataActions} from '~/shared/store/storeModules/errorDialogData';

import {log as sentryLog, logError as sentryLogError, init as sentryInit, logStructured as sentryLogStructured} from './sentry';
import {initialPrints} from './consoleFilterMessage';

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

/**
 * Optional extras object that can be passed as the last argument to `logger.error`.
 *
 * Set `sendReport: true` to opt this call in to creating a Sentry Issue. Without it,
 * the error is only logged to the console and recorded as a Sentry breadcrumb.
 * Only pass `sendReport: true` for genuinely unexpected, unrecoverable errors that are
 * not already handled elsewhere in the call stack.
 */
export interface LoggerErrorExtras {
  sendReport?: boolean;
  fingerprint?: string;
  [key: string]: unknown;
}

export function initLogging() {
  initialPrints();
  sentryInit();
}

enum LogLevels {
  VERBOSE = 'verbose',
  LOG = 'log',
  WARN = 'warn',
  ERROR = 'error',
  ERROR_DIALOG = 'errorDialog',
}

const loggerLevelToConsoleLevel = {
  [LogLevels.VERBOSE]: 'info',
  [LogLevels.ERROR_DIALOG]: LogLevels.ERROR,
} as const;

const loggerLevelsToTrackAsErrors = {
  [LogLevels.ERROR]: true,
  [LogLevels.ERROR_DIALOG]: true,
};

const allowConsoleLogs = !IS_PRODUCTION || localStorage?.allowConsoleLogs === 'true';

function isErrorLevel(logLevel: LogLevels): logLevel is keyof typeof loggerLevelsToTrackAsErrors {
  return Object.keys(loggerLevelsToTrackAsErrors).includes(logLevel);
}

function isNotConsoleLevel(logLevel: LogLevels): logLevel is keyof typeof loggerLevelToConsoleLevel {
  return Object.keys(loggerLevelToConsoleLevel).includes(logLevel);
}

function trackError({
  loggerName,
  errorOrMessage,
  args,
}: {
  loggerName: string;
  errorOrMessage: string | Error;
  args: any;
}) {
  const error = isError(errorOrMessage) ? errorOrMessage : new Error(errorOrMessage);

  try {
    if (args?.find((item: any) => item?.status === 401)) {
      return;
    }

    // Sentry Issue reporting is opt-in: an Issue is only created when a caller
    // explicitly passes `{sendReport: true}`. Handled/controlled errors still
    // log to the console and leave a breadcrumb below, but do not create Issues.
    const shouldReport = args?.find((item: any) => item?.sendReport === true);

    if (shouldReport) {
      sentryLogError({loggerName, error, args});
    }

    // Always write a breadcrumb regardless of sendReport so debugging context
    // is preserved for post-mortem analysis.
    sentryLogStructured({
      loggerName,
      logLevel: 'error',
      message: error.message || (errorOrMessage || '').toString(),
      data: args?.length ? args : undefined,
    });
  } catch (err) {
    console.error(new Error('trackError in logging errored'), {error: err});
  }
}

function trackInfo({
  loggerName,
  logLevel,
  errorOrMessage,
  args,
}: {
  loggerName: string;
  logLevel: string;
  errorOrMessage: string | Error;
  args: any;
}) {
  try {
    sentryLog({loggerName, logLevel, errorOrMessage, args});
    sentryLogStructured({
      loggerName,
      logLevel,
      message: (errorOrMessage || '').toString(),
      data: args?.length ? args : undefined,
    });
  } catch (err) {
    console.error(new Error('trackInfo in logging errored'), {error: err});
  }
}

function loggerFn(logLevel: LogLevels, loggerName: string, errorOrMessage: string | Error, ...args: any) {
  try {
    if (isErrorLevel(logLevel) && loggerLevelsToTrackAsErrors[logLevel]) {
      trackError({loggerName, errorOrMessage, args});
    } else {
      trackInfo({loggerName, logLevel, errorOrMessage, args});
    }
    if (allowConsoleLogs) {
      const consoleLevel = isNotConsoleLevel(logLevel)
        ? (loggerLevelToConsoleLevel[
            logLevel
          ] as typeof loggerLevelToConsoleLevel[keyof typeof loggerLevelToConsoleLevel])
        : logLevel;
      console[consoleLevel](`$[${loggerName}]: ${errorOrMessage}`, ...args);
    }
  } catch (e) {
    console.error(new Error('Failed to log an error using the logger.'), {logLevel, loggerName, errorOrMessage, args});
  }
}

export function createLogger(loggerName: string) {
  // repeating code for the sake of intellisense
  return {
    [LogLevels.VERBOSE]: (errorOrMessage: string | Error, ...args: any) =>
      loggerFn(LogLevels.VERBOSE, loggerName, errorOrMessage, ...args),

    [LogLevels.LOG]: (errorOrMessage: string | Error, ...args: any) =>
      loggerFn(LogLevels.LOG, loggerName, errorOrMessage, ...args),

    [LogLevels.WARN]: (errorOrMessage: string | Error, ...args: any) =>
      loggerFn(LogLevels.WARN, loggerName, errorOrMessage, ...args),

    // `args` stays `any` for backwards compatibility (callers pass caught errors,
    // strings, etc. as extras). Pass a `LoggerErrorExtras` object to opt in to a
    // Sentry Issue, e.g. `logger.error('msg', {error, sendReport: true})`.
    [LogLevels.ERROR]: (errorOrMessage: string | Error, ...args: any) =>
      loggerFn(LogLevels.ERROR, loggerName, errorOrMessage, ...args),

    [LogLevels.ERROR_DIALOG]: (errorOrMessage: string | Error, ...args: any) => {
      if (!IS_PRODUCTION) {
        store.dispatch(errorDialogDataActions.addErrorsToDialog({errorOrMessage, args}));
      }

      return loggerFn(LogLevels.ERROR_DIALOG, loggerName, errorOrMessage, ...args);
    },
  };
}
